import { CssBaseline } from "@mui/material";
import { isEmpty, trimEnd } from "lodash-es";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  PrimaryAppBar,
  PrimaryFooter,
  PrimaryFormCard,
  PrimarySiteAccessCard,
  PrimaryToast,
  TimeShifterModal,
} from "react-common-ui-library";
import { LoadingSpinner } from "react-common-ui-library/FPACT/LoadingSpinner";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { resetDisplayToast } from "../../Features/UtilityDataSlice";
import {
  ACCESS_MANAGEMENT_DASHBOARD_ROUTE,
  ADMIN_DASHBOARD_ROUTE,
  BASE_ROUTE,
  CASE_HISTORY_ROUTE,
  CLIENT_MANAGEMENT_ROUTE,
  CONSUMER_DASHBOARD_ROUTE,
  CONSUMER_USER,
  DISCONTINUE,
  EMPTY,
  FIND_PROVIDER_URL,
  LOGIN_ROUTE,
  MAIN,
  MESSAGE_CENTRE,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  NOTIFICATIONS_ROUTE,
  NOTIFICATION_SCOPES,
  OVERIDE_ELIGIBILITY_ENABLED,
  PRIMARY_FORM_CARD_ID,
  PRIVACY_POLICY_LINK,
  PROFILE_BASE_ROUTE,
  PROVIDER_ADMIN_ROUTE,
  PROVIDER_REQUESTS_ROUTE,
  SLASH,
  SUBMIT_DATE_FORMAT,
  SUPER_ADMIN,
  TIME_SHIFTER_DATE_FORMAT,
  USER_ACCOUNT_ADMINISTRATION_ROUTE,
  noSwooshIcon,
  overlappingFooter,
  providerRequestsScopes,
  siteAccessCard,
} from "../../Constants/CommonConstants";
import {
  SLICE_AUTHORIZATION,
  THUNK_GET_PERFORM_LOGOUT,
} from "../../Constants/SliceAndThunkConstants";
import {
  clearAuthorizationApiResponse,
  performLogout,
} from "../../Features/AuthorizationSlice";
import { changeLocale } from "../../Features/LocaleSlice";
import { timeShifterOffset } from "../../Features/TimeShifterSlice";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import { manageAuthorizationSelector } from "../../Selectors/AuthorizationSelector";
import { manageLocaleSelector } from "../../Selectors/LocaleSelector";
import { manageUtilityDataSelector } from "../../Selectors/UtilityDataSelector";
import {
  displayActiveStatus,
  getInitials,
  navigateToFpactPage,
} from "../../Services/CommonServices";
import ConfigService from "../../Services/ConfigService";
import {
  StyledChildBox,
  StyledLoginChildBox,
  StyledParentBox,
  StyledPrimaryContainer,
  StyledSwooshIcon,
  StyledSwooshIconBox,
} from "../../Styles/Application/RoutesLayoutStyles";
import pageIds from "../../pageIds.json";
import { DateTime } from "luxon";
import { manageMyProfileSelector } from "../../Selectors/MyProfileSelector";
import {
  APPLICATION_STATUS_CONTINUE_INTAKE,
  APPLICATION_STATUS_START_INTAKE,
} from "../../Constants/MyProfileConstants";
import {
  getDashboardDetails,
  postMessageCenterInbox,
  postProviderMessageCenter,
} from "../../Features/MyProfileSlice";
import { getProviderRequests } from "../../Features/ProviderRequestsSlice";
import { manageProviderRequestsSelector } from "../../Selectors/ProviderRequestsSelector";

const FIND_PROVIDER_LINK_URL = ConfigService(FIND_PROVIDER_URL);
const PRIVACY_POLICY_LINK_URL = ConfigService(PRIVACY_POLICY_LINK);

/**
 * @description Layout page of the account, A parent route without a path, used exclusively for grouping child routes inside a specific layout.
 * @returns {ReactNode} Render the outlet for routes along with the common header, footer and container.
 */

function RoutesLayout() {
  const dispatch = useDispatch();

  const location = useLocation();

  const navigate = useNavigate();

  const intl = useIntl();

  const { toastId } = useSelector((state) => manageUtilityDataSelector(state));

  const isProfileRoute = location.pathname?.includes(PROFILE_BASE_ROUTE);

  const [openTimeShifterModal, setOpenTimeShifterModal] = useState(false);

  const [newDate, setNewDate] = useState("");

  const overrideEligibilityEnabled = ConfigService(OVERIDE_ELIGIBILITY_ENABLED);

  const pageIdDetails =
    pageIds[`${pageIds[location.pathname] ? location.pathname : BASE_ROUTE}`];

  const { language } = useSelector((state) => manageLocaleSelector(state));

  const { isLoading } = useSelector((state) =>
    manageUtilityDataSelector(state)
  );

  const {
    jwtToken,
    isDevtoolsEnabled,
    scopes,
    hapId,
    userName,
    firstName,
    lastName,
    isEntitledStaff,
  } = useSelector((state) => manageAuthorizationSelector(state));

  const { applicationStatus, isUnReadNotice, isUnreadNotification } =
    useSelector((state) => manageMyProfileSelector(state));
  const { isUnreadProviderRequest } = useSelector((state) =>
    manageProviderRequestsSelector(state)
  );

  const [avatarLabel, setAvatarLabel] = useState("");

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_AUTHORIZATION,
      THUNK_GET_PERFORM_LOGOUT
    )
  );

  const { isSuccess } = apiResponses;

  const isConsumerUser = scopes === CONSUMER_USER;

  const handleLocaleChange = (locale) => {
    dispatch(changeLocale(locale));
  };

  const onMessageClickHandler = () => {
    navigateToFpactPage(CONSUMER_DASHBOARD_ROUTE, MESSAGE_CENTRE);
  };

  const onDashboardClickHandler = () => {
    if (scopes === CONSUMER_USER) {
      navigateToFpactPage(CONSUMER_DASHBOARD_ROUTE);
    } else if (scopes === SUPER_ADMIN) {
      navigateToFpactPage(PROVIDER_ADMIN_ROUTE, ADMIN_DASHBOARD_ROUTE);
    } else {
      if (isEntitledStaff) {
        navigateToFpactPage(
          PROVIDER_ADMIN_ROUTE,
          ACCESS_MANAGEMENT_DASHBOARD_ROUTE
        );
      }
      navigateToFpactPage(PROVIDER_ADMIN_ROUTE);
    }
  };

  const onAccountSettingClickHandler = () => {
    navigate(`/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}`);
  };

  const onProviderRequestsClickHandler = () => {
    navigateToFpactPage(PROVIDER_ADMIN_ROUTE, PROVIDER_REQUESTS_ROUTE);
  };

  const onLogOutClickHandler = () => {
    dispatch(performLogout());
  };

  const onCaseHistoryClickHandler = () => {
    navigateToFpactPage(CASE_HISTORY_ROUTE);
  };

  const onDiscontinueClickHandler = () => {
    navigateToFpactPage(CONSUMER_DASHBOARD_ROUTE, DISCONTINUE);
  };

  const onClientManagementClickHandler = () => {
    navigateToFpactPage(CLIENT_MANAGEMENT_ROUTE);
  };

  const onUserAccountAdministrationClick = () => {
    navigateToFpactPage(USER_ACCOUNT_ADMINISTRATION_ROUTE);
  };

  const shifterDateFormat = (date) => {
    if (date) {
      return DateTime.fromFormat(date, SUBMIT_DATE_FORMAT).toFormat(
        TIME_SHIFTER_DATE_FORMAT
      );
    }

    return null;
  };

  const onModifyTimeShifterClickHandler = () => {
    setOpenTimeShifterModal(true);
  };

  const onTimeShifterModalCancelButtonClickHandler = () => {
    setOpenTimeShifterModal(false);
  };

  const onTimeShifterModalConfirmButtonClickHandler = (data) => {
    const payload = {
      userid: userName,
      shiftedDate: shifterDateFormat(data.timeshifterDate),
    };

    setNewDate(data.timeshifterDate);
    dispatch(timeShifterOffset(payload));
    setOpenTimeShifterModal(false);
  };

  const findProviderLinkClickHandler = () => {
    window.open(FIND_PROVIDER_LINK_URL);
  };

  const privacyPolicyLinkClickHandler = () => {
    window.open(PRIVACY_POLICY_LINK_URL);
  };

  const onNotificationClickHandler = () => {
    navigateToFpactPage(PROVIDER_ADMIN_ROUTE, NOTIFICATIONS_ROUTE);
  };

  const getShowCaseHistoryStatus = () => {
    return (
      isConsumerUser &&
      !(
        applicationStatus === APPLICATION_STATUS_START_INTAKE ||
        applicationStatus === APPLICATION_STATUS_CONTINUE_INTAKE
      )
    );
  };

  useEffect(() => {
    setAvatarLabel(`${getInitials(firstName)}${getInitials(lastName)}`);
  }, [firstName, lastName]);

  /**
   * The below variables are used to handle different layouts based on the path.
   */
  const isOverLappingFooter = useMemo(() => {
    return overlappingFooter.includes(trimEnd(location.pathname, SLASH));
  }, [location.pathname]);

  const isNoSwooshRequired = useMemo(() => {
    return noSwooshIcon.includes(trimEnd(location.pathname, SLASH));
  }, [location.pathname]);

  const isSiteAccessCard = useMemo(() => {
    return siteAccessCard.includes(trimEnd(location.pathname, SLASH));
  }, [location.pathname]);

  const renderCardOutlet = useMemo(() => {
    if (isSiteAccessCard) {
      return (
        <PrimarySiteAccessCard>
          <Outlet />
        </PrimarySiteAccessCard>
      );
    }
    return (
      <PrimaryFormCard id={PRIMARY_FORM_CARD_ID}>
        <Outlet />
      </PrimaryFormCard>
    );
  }, [isSiteAccessCard]);

  useEffect(() => {
    if (isSuccess && isProfileRoute) {
      navigate(`/${LOGIN_ROUTE}`);
      dispatch(clearAuthorizationApiResponse(THUNK_GET_PERFORM_LOGOUT));
    }
  }, [isSuccess, navigate, isProfileRoute, dispatch]);

  useEffect(() => {
    if (isConsumerUser) {
      dispatch(getDashboardDetails());
      dispatch(postMessageCenterInbox());
    }
  }, [dispatch, isConsumerUser]);

  useEffect(() => {
    if (NOTIFICATION_SCOPES.includes(scopes)) {
      dispatch(postProviderMessageCenter());
    }
  }, [dispatch, scopes]);

  useEffect(() => {
    if (providerRequestsScopes.includes(scopes) && overrideEligibilityEnabled) {
      dispatch(getProviderRequests());
    }
  }, [dispatch, scopes, overrideEligibilityEnabled]);

  return (
    <Fragment>
      <StyledParentBox isOverLappingFooter={isOverLappingFooter}>
        <PrimaryAppBar
          isAuthenticated={!isEmpty(jwtToken) && isProfileRoute}
          onLocaleChangeHandler={handleLocaleChange}
          currentLocale={language}
          scopes={scopes}
          messageBadge={isUnReadNotice}
          messageClick={onMessageClickHandler}
          dashboardClick={onDashboardClickHandler}
          avatarLabel={avatarLabel}
          accountSettingClick={onAccountSettingClickHandler}
          logOutClick={onLogOutClickHandler}
          caseHistoryClick={onCaseHistoryClickHandler}
          discontinueClick={onDiscontinueClickHandler}
          userAccountClick={onUserAccountAdministrationClick}
          clientManagementClick={onClientManagementClickHandler}
          showCaseHistory={getShowCaseHistoryStatus()}
          showDiscontinue={hapId && displayActiveStatus(applicationStatus)}
          isConsumerDashboard={scopes.includes(CONSUMER_USER)}
          notificationBadge={isUnreadNotification}
          notificationClick={onNotificationClickHandler}
          isEntitledStaff={isEntitledStaff}
          providerRequestsBadge={isUnreadProviderRequest}
          providerRequestsClick={onProviderRequestsClickHandler}
          showProviderRequests={overrideEligibilityEnabled}
        />
        {!isOverLappingFooter && !isNoSwooshRequired ? (
          <StyledChildBox role={MAIN}>{renderCardOutlet}</StyledChildBox>
        ) : null}
        {isOverLappingFooter && !isNoSwooshRequired ? (
          <Fragment>
            <StyledPrimaryContainer role={MAIN}>
              <Outlet />
            </StyledPrimaryContainer>
            <StyledSwooshIconBox>
              <StyledSwooshIcon />
            </StyledSwooshIconBox>
          </Fragment>
        ) : null}
        {isNoSwooshRequired ? (
          <StyledLoginChildBox role={MAIN}>
            <Outlet />
          </StyledLoginChildBox>
        ) : null}
        <CssBaseline />
        <PrimaryFooter
          lowerEnvironment={isDevtoolsEnabled && isProfileRoute}
          lowerEnvironmentProps={{
            onModifyTimeShifterClick: onModifyTimeShifterClickHandler,
            buildVersionId: `${process.env.BUILD_TAG}`,
            pageName: pageIdDetails.pageName,
            newDate: newDate,
          }}
          showApplicationState={false}
          linkOnClickHandler={findProviderLinkClickHandler}
          privacyPolicyLinkClick={privacyPolicyLinkClickHandler}
        />
        <TimeShifterModal
          open={openTimeShifterModal}
          confirmOffsetBtnClick={onTimeShifterModalConfirmButtonClickHandler}
          cancelBtnClick={onTimeShifterModalCancelButtonClickHandler}
          newDate={newDate}
        />
      </StyledParentBox>
      {isLoading ? <LoadingSpinner open={isLoading} /> : null}
      <PrimaryToast
        isOpen={Boolean(toastId)}
        setIsOpen={() => {
          dispatch(resetDisplayToast());
        }}
        id={toastId}
      >
        {toastId ? intl.formatMessage({ id: toastId }) : EMPTY}
      </PrimaryToast>
    </Fragment>
  );
}

export default RoutesLayout;
